import { type FC, useState } from "react";
import { EyeOff, Eye, Info } from "lucide-react";

import { FieldError } from "@/shared/components/ui/FieldError";
import { CurrencyInput } from "@/shared/components/ui/CurrencyInput";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { BalanceEstimateSection } from "@/shared/components/EstateItems/BalanceEstimateSection";
import { ACCOUNT_TYPES } from "@/shared/constants/estateCategories";
import { OWNERSHIP_TYPES } from "@/shared/constants/ownershipTypes";
import { resolveBalance } from "@/shared/utils/balanceEstimates";
import { currencyInput } from "@/shared/utils/currencyInput";

import type { EstateItem, CreateEstateItemRequest } from "@/shared/types/estateTypes";

export const FINANCIAL_CATEGORIES = [
  "Bank accounts",
  "Brokerage accounts",
  "Tax-advantaged savings",
  "Retirement accounts",
  "Crypto accounts",
  "NFTs",
  "Other digital assets",
] as const;

type FinancialCategory = (typeof FINANCIAL_CATEGORIES)[number];

export interface AccountTypeOption {
  label: string;
  itemType: string;
  category: string;
}

export const ACCOUNT_TYPE_OPTIONS: AccountTypeOption[] = FINANCIAL_CATEGORIES.flatMap(
  (category) => {
    const types = ACCOUNT_TYPES[category] ?? [];
    return types.map((itemType) => ({
      label: itemType,
      itemType,
      category,
    }));
  },
);

export const CO_OWNER_OWNERSHIP_TYPES = new Set([
  "jtwros",
  "tbe",
  "tic",
  "community_property",
  "community_property_survivorship",
]);

export interface FinancialAccountFormValues {
  accountType: string;
  category: string;
  institution: string;
  balance: string;
  accountLast4: string;
  balanceUnknown: boolean;
  estimateRange: string;
  ownershipType: string;
  coOwners: string;
}

export interface FinancialAccountFieldErrors {
  institution?: string;
  accountType?: string;
}

export function validateFinancialAccountFields(
  form: FinancialAccountFormValues,
): FinancialAccountFieldErrors {
  if (!form.institution.trim() && !form.accountType) {
    return { institution: "Please enter an institution or select an account type" };
  }
  return {};
}

export function financialAccountToItemFields(
  form: FinancialAccountFormValues,
): Partial<CreateEstateItemRequest> {
  const metaFields: Record<string, unknown> = {};
  if (form.institution.trim()) metaFields.institution = form.institution.trim();
  if (form.coOwners.trim()) metaFields.co_owners = form.coOwners.trim();
  if (form.accountLast4.trim()) metaFields.account_last4 = form.accountLast4.trim();

  const identifiers = form.accountLast4.trim()
    ? { account_last4: form.accountLast4.trim() }
    : undefined;

  const balance = resolveBalance(form.balanceUnknown, form.estimateRange, form.balance);

  return {
    item_name: form.institution.trim() || form.accountType,
    item_type: form.accountType || undefined,
    category: form.category,
    balance,
    is_estimated: form.balanceUnknown,
    balance_estimate_range: form.balanceUnknown ? form.estimateRange : null,
    ownership: form.ownershipType || undefined,
    ...(Object.keys(metaFields).length > 0 ? { metadata: metaFields } : {}),
    ...(identifiers ? { identifiers } : {}),
  };
}

export function parseItemToForm(item: EstateItem): FinancialAccountFormValues {
  const wasEstimated = item.is_estimated === true;
  const metaInstitution = (item.metadata?.institution as string) ?? "";
  const nameIsAccountType = item.item_name === item.item_type;
  const institution = metaInstitution || (nameIsAccountType ? "" : (item.item_name ?? ""));

  return {
    accountType: item.item_type ?? "",
    category: item.category,
    institution,
    balance: wasEstimated ? "" : item.balance ? String(item.balance) : "",
    accountLast4:
      (item.identifiers?.account_last4 as string) ?? (item.metadata?.account_last4 as string) ?? "",
    balanceUnknown: wasEstimated,
    estimateRange: wasEstimated && item.balance_estimate_range ? item.balance_estimate_range : "",
    ownershipType: item.ownership ?? "",
    coOwners: (item.metadata?.co_owners as string) ?? "",
  };
}

export function isFinancialCategory(category: string): category is FinancialCategory {
  return (FINANCIAL_CATEGORIES as readonly string[]).includes(category);
}

interface FinancialAccountFormProps {
  form: FinancialAccountFormValues;
  onChange: (form: FinancialAccountFormValues) => void;
  fieldErrors?: FinancialAccountFieldErrors;
}

export const FinancialAccountForm: FC<FinancialAccountFormProps> = ({
  form,
  onChange,
  fieldErrors,
}) => {
  const [identifierMasked, setIdentifierMasked] = useState(false);
  const showCoOwners = form.ownershipType && CO_OWNER_OWNERSHIP_TYPES.has(form.ownershipType);

  const balanceInput = currencyInput(form.balance, (val) => onChange({ ...form, balance: val }));

  const filteredOptions = ACCOUNT_TYPE_OPTIONS.filter((o) => o.category === form.category);

  return (
    <div className="flex flex-col gap-4">
      {filteredOptions.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <Label className="text-sm font-medium">Account type</Label>
          <Select
            value={form.accountType}
            onValueChange={(val) => {
              const opt = ACCOUNT_TYPE_OPTIONS.find((o) => o.itemType === val);
              onChange({
                ...form,
                accountType: val,
                category: opt?.category ?? form.category,
              });
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select account type" />
            </SelectTrigger>
            <SelectContent>
              {filteredOptions.map((opt) => (
                <SelectItem key={opt.itemType} value={opt.itemType}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <Label className="text-sm font-medium">Institution</Label>
        <Input
          value={form.institution}
          onChange={(e) => onChange({ ...form, institution: e.target.value })}
          placeholder="e.g., Chase, Fidelity, Vanguard"
          className={fieldErrors?.institution ? "border-error" : ""}
        />
        <FieldError message={fieldErrors?.institution} />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <Label className="text-sm font-medium">Balance</Label>
          <CurrencyInput
            value={balanceInput.displayValue}
            onChange={balanceInput.handleChange}
            placeholder="0.00"
            disabled={form.balanceUnknown}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-sm font-medium">
            Last 4 of account number <span className="text-secondary">(optional)</span>
          </Label>
          <div className="relative">
            <Input
              value={
                identifierMasked && form.accountLast4
                  ? "\u2022\u2022\u2022\u2022"
                  : form.accountLast4
              }
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, "");
                setIdentifierMasked(false);
                onChange({ ...form, accountLast4: digits });
              }}
              onFocus={() => setIdentifierMasked(false)}
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="e.g., 7890"
              maxLength={4}
              className="pr-9"
            />
            {form.accountLast4 && (
              <button
                type="button"
                onClick={() => setIdentifierMasked((m) => !m)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-tertiary hover:text-primary"
                aria-label={identifierMasked ? "Show number" : "Hide number"}
              >
                {identifierMasked ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              </button>
            )}
          </div>
        </div>
      </div>

      <BalanceEstimateSection
        balanceUnknown={form.balanceUnknown}
        estimateRange={form.estimateRange}
        onBalanceUnknownChange={(checked) => {
          onChange({
            ...form,
            balanceUnknown: checked,
            balance: checked ? "" : form.balance,
            estimateRange: checked ? form.estimateRange : "",
          });
        }}
        onEstimateRangeChange={(val) => onChange({ ...form, estimateRange: val })}
      />

      <div className="flex flex-col gap-1.5">
        <Label className="flex items-center gap-1 text-sm font-medium">
          Ownership type <span className="text-secondary">(optional)</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3.5 w-3.5 text-tertiary" />
              </TooltipTrigger>
              <TooltipContent>
                How the account was owned determines who inherits it and how it transfers.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </Label>
        <Select
          value={form.ownershipType}
          onValueChange={(val) => {
            onChange({
              ...form,
              ownershipType: val,
              coOwners: CO_OWNER_OWNERSHIP_TYPES.has(val) ? form.coOwners : "",
            });
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select ownership type" />
          </SelectTrigger>
          <SelectContent>
            {OWNERSHIP_TYPES.map((t) => (
              <SelectItem key={t.slug} value={t.slug}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {showCoOwners && (
        <div className="flex flex-col gap-1.5">
          <Label className="flex items-center gap-1 text-sm font-medium">
            Co-owner's name(s) <span className="text-secondary">(optional)</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3.5 w-3.5 text-tertiary" />
                </TooltipTrigger>
                <TooltipContent>The other person(s) who co-owned this account.</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </Label>
          <Input
            placeholder="e.g., Jane Smith, Robert Johnson"
            value={form.coOwners}
            onChange={(e) => onChange({ ...form, coOwners: e.target.value })}
          />
        </div>
      )}
    </div>
  );
};
